import _ from 'lodash';

import { TeamMember, TeamMemberStatus } from '../../features/team/team.types';
import {
  normalizeInvitation,
  normalizeSecurityGroups,
} from '../../features/team/team.utils';
import {
  InvitationPayload,
  MemberEmail,
  MemberPayload,
} from '../../types/members.types';
import { transport } from '../transport';

import {
  BASE_PATH,
  INVITATIONS_PATH,
  MEMBERS_PATH,
  SECURITY_GROUPS_PATH,
  PROJECT_PATH,
} from './config';
import { WixMpResponse } from '../../types/http-responses';

const normalizeTeamMember = (
  organizationId: string,
  member: MemberPayload,
): TeamMember => {
  const membership = _.find(member.memberships, { organizationId });
  const securityGroups = _.get(membership, 'securityGroups', []);

  const primaryEmail = _(member.contactChannels.emails)
    .filter((email) => email.primary && email.public)
    .head() as MemberEmail;

  return {
    id: member.id,
    avatarUrl: member.avatar || '',
    name: _.trim(member.name),
    primaryEmail,
    securityGroups: normalizeSecurityGroups(securityGroups),
    status: TeamMemberStatus.Accepted,
  };
};

function getTeamMembers({ organizationId }: { organizationId: string }) {
  return transport
    .get<WixMpResponse<MemberPayload[]>>(
      `${BASE_PATH}/${organizationId}${MEMBERS_PATH}`,
    )
    .then(({ data }) =>
      _.map(data.payload, _.partial(normalizeTeamMember, organizationId)),
    );
}

function getTeamMembersForProject({
  organizationId,
  projectId,
}: {
  organizationId: string;
  projectId: string;
}) {
  return transport
    .get<WixMpResponse<MemberPayload[]>>(
      `${BASE_PATH}/${organizationId}/projects/${projectId}${MEMBERS_PATH}`,
    )
    .then(({ data }) =>
      _.map(data.payload, _.partial(normalizeTeamMember, organizationId)),
    );
}

interface EditTeamMember {
  memberId: string;
  organizationId: string;
  securityGroups: string[];
}

function editTeamMember({
  memberId,
  organizationId,
  securityGroups,
}: EditTeamMember) {
  const uniqueSecurityGroups = _.uniq(securityGroups);

  return transport.put(
    `${BASE_PATH}/${organizationId}${MEMBERS_PATH}/${memberId}${SECURITY_GROUPS_PATH}`,
    {
      securityGroups: uniqueSecurityGroups,
    },
  );
}

interface DeleteTeamMember {
  memberId: string;
  organizationId: string;
}

function deleteTeamMember({ organizationId, memberId }: DeleteTeamMember) {
  return transport.delete(
    `${BASE_PATH}/${organizationId}${MEMBERS_PATH}/${memberId}`,
  );
}

function getInvitations({
  organizationId,
  projectId,
}: {
  organizationId: string;
  projectId?: string;
}) {
  const url =
    (projectId &&
      `${BASE_PATH}/${organizationId}${PROJECT_PATH}/${projectId}${INVITATIONS_PATH}`) ||
    `${BASE_PATH}/${organizationId}${INVITATIONS_PATH}`;

  return transport
    .get<WixMpResponse<InvitationPayload[]>>(url)
    .then(({ data }) =>
      _.reduce(
        data.payload,
        (acc: TeamMember[], value: InvitationPayload): TeamMember[] => {
          if (value.status === TeamMemberStatus.Pending) {
            acc.push(normalizeInvitation(value));
          }

          return acc;
        },
        [],
      ),
    );
}

function deleteTeamInvitation({ organizationId, memberId }: DeleteTeamMember) {
  return transport.delete(
    `${BASE_PATH}/${organizationId}${INVITATIONS_PATH}/${memberId}`,
  );
}

export const teamMembersService = {
  deleteTeamInvitation,
  deleteTeamMember,
  editTeamMember,
  getInvitations,
  getTeamMembers,
  getTeamMembersForProject,
};
